import { useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Badge, Box, Grid, InputAdornment } from '@mui/material';
import {
  Button,
  FormikInputString,
  H2,
  Loader,
  P2,
  UnsavedChangesPopup,
  Notification,
  FormikInputPhone,
  Avatar,
  Icon,
  FormikInputZip,
} from 'src/components';
import { themeColors } from 'src/theme';
import { TenantType } from 'src/types';
import { tenantScheme } from 'src/utils/validationSchemes';
import { useUpdateTenant } from 'src/api/tenant';
import UploadTenantLogoModal from './UploadLogoModal';

interface AboutTabProps {
  tenant: TenantType;
  isTenantLoading: boolean;
  refetchTenants: () => void;
}

const AboutCompanyTab = ({ tenant, isTenantLoading, refetchTenants }: AboutTabProps) => {
  const { t } = useTranslation();
  const blocker = useBlocker(({ currentLocation, nextLocation }) => formik.dirty && currentLocation.pathname !== nextLocation.pathname);
  const [isUpdated, setIsUpdated] = useState(false);
  const [openAvatarPopup, setOpenAvatarPopup] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: tenant.title || '',
      address1_country: tenant.address1?.country || '',
      address1_city: tenant.address1?.city || '',
      address1_street: tenant.address1?.street || '',
      address1_state: tenant.address1?.state || '',
      address1_zip: tenant.address1?.zip || '',
      address2_country: tenant.address2?.country || '',
      address2_city: tenant.address2?.city || '',
      address2_street: tenant.address2?.street || '',
      address2_state: tenant.address2?.state || '',
      address2_zip: tenant.address2?.zip || '',
      phone: tenant.phone || '',
      website: tenant.website?.replace(/^(https?:\/\/)/i, '') || '',
      contact_name: tenant.pointOfContact?.name || '',
      contact_phone: tenant.pointOfContact?.number || '',
      // slug: tenant.slug || '',
    },
    validationSchema: tenantScheme,
    onSubmit: async (data) => {
      const newCompanyPayload = {
        id: tenant.id,
        title: data.title,
        address1: {
          country: data.address1_country,
          city: data.address1_city,
          street: data.address1_street,
          state: data.address1_state,
          zip: data.address1_zip,
        },
        address2: {
          country: data.address2_country,
          city: data.address2_city,
          street: data.address2_street,
          state: data.address2_state,
          zip: data.address2_zip,
        },
        phone: data.phone,
        website: data.website ? `https://${data.website}` : null,
        pointOfContact: {
          name: data.contact_name,
          number: data.contact_phone ? data.contact_phone : null,
        },
        // slug: data.slug,
      };
      mutateUpdateTenant({ id: tenant.id, body: newCompanyPayload });
    },
  });

  const { mutate: mutateUpdateTenant, isPending: isTenantUpdating } = useUpdateTenant({
    meta: {
      setError: formik.setErrors,
    },
    onSuccess: () => {
      formik.resetForm();
      setIsUpdated(true);
      refetchTenants();
    },
  });

  useEffect(() => {
    // eslint-disable-next-line consistent-return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (formik.dirty) {
        const confirmationMsg = 'You have unsaved changes.';
        e.returnValue = confirmationMsg;

        return confirmationMsg;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formik.dirty]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
      {isTenantUpdating || isTenantLoading ? (
        <Loader />
      ) : (
        <>
          <Badge
            sx={{ mb: 6 }}
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              // isOwner && (
              <Button
                onClick={() => setOpenAvatarPopup(true)}
                size="small"
                sx={{ borderRadius: 100, minWidth: 'unset', width: '32px', height: '32px', p: 0 }}
              >
                {tenant.logoUrl ? <Icon name="edit" size={18} /> : <Icon name="add" size={18} />}
              </Button>
            }
          >
            <Avatar name={tenant.title} src={tenant.logoUrl || ''} sx={{ borderRadius: 100, width: '100px', height: '100px' }} />
          </Badge>

          <Box
            sx={{
              maxWidth: '622px',
              borderRadius: '4px',
              border: `1px solid ${themeColors.grayMedium}`,
            }}
          >
            <Box
              sx={{
                backgroundColor: themeColors.grayLight,
                px: 3,
                py: 4,
              }}
            >
              <Box sx={{ my: 2 }}>
                <H2>About Company</H2>
              </Box>

              <Grid container rowSpacing={0} columnSpacing={3}>
                <Grid item xs={12}>
                  <FormikInputString formik={formik} field="title" label={t('companyDashboardPage.companyName')} autoFocus />
                </Grid>
                <Grid item xs={12} sx={{ mb: 1 }}>
                  <P2 sx={{ fontWeight: 600 }}>{t('companyDashboardPage.address1')}</P2>
                </Grid>
                <Grid item xs={6}>
                  <FormikInputString formik={formik} field="address1_country" label={t('companyDashboardPage.country')} />
                </Grid>
                <Grid item xs={6}>
                  <FormikInputString formik={formik} field="address1_city" label={t('companyDashboardPage.city')} />
                </Grid>
                <Grid item xs={12}>
                  <FormikInputString formik={formik} field="address1_street" label={t('companyDashboardPage.street')} />
                </Grid>
                <Grid item xs={6}>
                  <FormikInputString formik={formik} field="address1_state" label={t('companyDashboardPage.state')} />
                </Grid>
                <Grid item xs={6}>
                  <FormikInputZip formik={formik} field="address1_zip" label={t('companyDashboardPage.zip')} />
                </Grid>

                <Grid item xs={12} sx={{ mb: 1 }}>
                  <P2 sx={{ fontWeight: 600 }}>
                    {t('companyDashboardPage.address2')}{' '}
                    <span style={{ color: themeColors.grayPlaceholders }}>({t('companyDashboardPage.optional')})</span>
                  </P2>
                </Grid>
                <Grid item xs={6}>
                  <FormikInputString formik={formik} field="address2_country" label={t('companyDashboardPage.country')} />
                </Grid>
                <Grid item xs={6}>
                  <FormikInputString formik={formik} field="address2_city" label={t('companyDashboardPage.city')} />
                </Grid>
                <Grid item xs={12}>
                  <FormikInputString formik={formik} field="address2_street" label={t('companyDashboardPage.street')} />
                </Grid>
                <Grid item xs={6}>
                  <FormikInputString formik={formik} field="address2_state" label={t('companyDashboardPage.state')} />
                </Grid>
                <Grid item xs={6}>
                  <FormikInputZip formik={formik} field="address2_zip" label={t('companyDashboardPage.zip')} />
                </Grid>
                <Grid item xs={12}>
                  <FormikInputPhone formik={formik} field="phone" label={t('companyDashboardPage.phone')} />
                </Grid>
                <Grid item xs={12}>
                  <FormikInputString
                    formik={formik}
                    field="website"
                    label={t('companyDashboardPage.website')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">https://</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mb: 1 }}>
                  <P2 sx={{ fontWeight: 600 }}>
                    {t('companyDashboardPage.pointOfContact')}{' '}
                    <span style={{ color: themeColors.grayPlaceholders }}>({t('companyDashboardPage.optional')})</span>
                  </P2>
                </Grid>
                <Grid item xs={12}>
                  <FormikInputString formik={formik} field="contact_name" label={t('companyDashboardPage.fullName')} />
                </Grid>
                <Grid item xs={12}>
                  <FormikInputPhone formik={formik} field="contact_phone" label={t('companyDashboardPage.phone')} />
                </Grid>

                {/* // ERMA-3239: this URL functionality is not needed for now */}
                {/* <Grid item xs={12} sx={{ mb: 2 }}>
                <P2 sx={{ fontWeight: 600 }}>{t('companyDashboardPage.companyUrl')}</P2>
                <P3 component="p" sx={{ color: themeColors.grayDark, lineHeight: 1.4 }}>
                  {t('companyDashboardPage.companyUrlDesc')}
                </P3>
              </Grid>
              <Grid item xs={12}>
                <FormikInputString
                  formik={formik}
                  label={t('companyDashboardPage.companyUrl')}
                  placeholder="mywebsite"
                  field="slug"
                  disabled={!!user.tenantId}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ marginRight: 0 }}>
                        <P1R sx={{ color: themeColors.grayPlaceholders }}>https://</P1R>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginLeft: 0 }}>
                        <P1R sx={{ color: themeColors.grayPlaceholders }}>{`.${config.DOMAIN}`}</P1R>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {user.tenantId && (
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <P3 component="p" sx={{ color: themeColors.grayDark, lineHeight: 1.4 }}>
                    {t('companyDashboardPage.urlChangeWarning')}
                  </P3>
                </Grid>
              )} */}
              </Grid>
            </Box>

            <Box
              sx={{
                width: '100%',
                backgroundColor: themeColors.white,
                display: 'flex',
                justifyContent: 'flex-end',
                flexDirection: { xs: 'column', md: 'row' },
                px: 3,
                py: 4,
              }}
            >
              <Button
                disabled={!formik.dirty}
                onClick={() => formik.resetForm()}
                primary={false}
                sx={{ textTransform: 'capitalize', mr: 2, width: { xs: '100%', md: '200px' } }}
              >
                {t('companyDashboardPage.cancel')}
              </Button>

              <Button
                disabled={!formik.dirty}
                onClick={() => formik.handleSubmit()}
                sx={{ textTransform: 'capitalize', width: { xs: '100%', md: '200px' } }}
              >
                Update
              </Button>
            </Box>
          </Box>
        </>
      )}
      {blocker.state === 'blocked' && <UnsavedChangesPopup onProceed={blocker.proceed} onCancel={blocker.reset} />}
      <Notification
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isUpdated}
        onClose={() => setIsUpdated(false)}
      >
        <P2 sx={{ color: themeColors.white, textTransform: 'none' }}>{t('companyDashboardPage.dataIsSaved')}</P2>
      </Notification>
      {openAvatarPopup && <UploadTenantLogoModal tenant={tenant} onClose={() => setOpenAvatarPopup(false)} />}
    </Box>
  );
};

export default AboutCompanyTab;
